/** Client-side message identity for the agent transport.
 *
 * The frontend mints one UUID per logical send, keyed on the transport
 * command's object identity (the same command instance flows through both the
 * request body and the runtime's `pendingCommands`). The id travels to the
 * backend, onto the checkpoint/archive message, and back on the stream echo —
 * one identity end to end, so optimistic dedup is exact and a retry of a
 * failed turn can reuse the original id (the backend upserts by id instead of
 * duplicating the question).
 */

const commandMessageIds = new WeakMap<object, string>()

export function messageIdFor(command: object): string {
  let id = commandMessageIds.get(command)
  if (!id) {
    id = crypto.randomUUID()
    commandMessageIds.set(command, id)
  }
  return id
}

/** A transport add-message command carrying a chosen id — used by the failed-
 * turn retry chip, which must re-send under the ORIGINAL message id. */
export function buildUserMessageCommand(
  text: string,
  id?: string,
  options?: { freshContext: boolean }
) {
  const command = {
    type: 'add-message' as const,
    message: { role: 'user' as const, parts: [{ type: 'text' as const, text }] },
    parentId: null,
    sourceId: null,
    ...(options?.freshContext ? { fresh_context: true } : {}),
  }
  if (id) commandMessageIds.set(command, id)
  return command
}
